import { useDeepCompareEffect, useList } from "@react-hookz/web";
import first from "lodash/first";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useIntersection = ({ root, rootMargin, threshold }: IntersectionObserverInit) => {
  const [elements, { push: pushElements }] = useList<HTMLElement>([]);
  const [intersections, { clear: clearIntersections, set: setIntersections }] =
    useList<IntersectionObserverEntry>([]);
  const [shouldDisable, setShouldDisable] = useState<boolean>(false);

  const element = useMemo(() => first(elements) ?? null, [elements]);
  const intersection = useMemo(() => first(intersections) ?? null, [intersections]);
  const shouldObserve = useMemo(
    () => !shouldDisable && elements.length > 0 && typeof IntersectionObserver === "function",
    [elements.length, shouldDisable],
  );

  const captureIntersectionElement = useCallback(
    (dom: HTMLElement | null) => {
      if (!dom || elements.some(element => element.isSameNode(dom))) return;
      pushElements(dom);
      clearIntersections();
    },
    [clearIntersections, elements, pushElements],
  );

  useEffect(() => {
    if (shouldDisable) {
      clearIntersections();
    }
  }, [clearIntersections, shouldDisable]);

  useDeepCompareEffect(() => {
    if (!shouldObserve) return;

    const observer = new IntersectionObserver(
      entries => {
        setIntersections(entries);
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );
    elements.forEach(element => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [elements, root, rootMargin, setIntersections, shouldObserve, threshold]);

  return {
    captureIntersectionElement,
    element,
    elements,
    intersection,
    intersections,
    setShouldDisable,
  };
};
